import { useAuth } from "@clerk/clerk-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY } from "@/lib/supabaseClient";

export interface SaveSnapshotResult {
  captured: number;
  failed: number;
  business_date: string;
  message?: string;
}

/**
 * Returns a mutation that saves the live waitlist of every pinned store on
 * demand via the waitlist-snapshot edge function (source='manual', brief
 * decision 5a). On success it invalidates the history query so a freshly saved
 * capture shows up immediately.
 */
export const useSaveSnapshot = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<SaveSnapshotResult, Error>({
    mutationFn: async () => {
      const jwt = await getToken({ template: "supabase" });
      if (!jwt) throw new Error("Not authenticated. Sign in and try again.");

      const res = await fetch(`${SUPABASE_URL}/functions/v1/waitlist-snapshot`, {
        method: "POST",
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${jwt}`,
          apikey: SUPABASE_PUBLISHABLE_KEY,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ source: "manual" }),
      });

      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        throw new Error(`Request failed (${res.status}). The response was not JSON.`);
      }
      const data = body as Partial<SaveSnapshotResult> & { error?: string };
      if (!res.ok) {
        throw new Error(data?.error || `Request failed (${res.status}).`);
      }
      return {
        captured: data.captured ?? 0,
        failed: data.failed ?? 0,
        business_date: data.business_date ?? "",
        message: data.message,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pos-waitlist-snapshots"] });
    },
  });
};
